export default function DiagnosisSection() {
  const steps = [
    { num: '01', title: 'Clinical Recognition', desc: 'Recognition of characteristic facial features, skeletal abnormalities, and cardiac findings. Many patients are initially misdiagnosed with Noonan syndrome or other RASopathies due to overlapping features.' },
    { num: '02', title: 'Ruling Out Other Conditions', desc: 'Prior negative genetic testing commonly includes karyotype, chromosomal microarray, PTPN11 sequencing, fragile X, and subtelomeric FISH. CSCFS is frequently a diagnosis reached after exclusion of more common syndromes.' },
    { num: '03', title: 'Whole Exome Sequencing (WES)', desc: 'Whole exome sequencing of the patient and both parents (trio-WES) is currently the most effective method for identifying MAP3K7 variants. WES has identified all genetically confirmed CSCFS cases in the literature.' },
    { num: '04', title: 'Sanger Confirmation', desc: 'Identified variants are confirmed by Sanger sequencing. De novo variants are confirmed by verifying absence of the variant in both biological parents.' },
    { num: '05', title: 'Variant Classification', desc: 'Variants are classified using ACMG/AMP guidelines. All confirmed CSCFS variants are located in the kinase domain of MAP3K7. De novo status (PS2), rarity in controls (PM2), and protein length/function changes (PM4) support pathogenicity.' },
  ];

  return (
    <section id="diagnosis" className="py-16 bg-white">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-xl">
            <i className="ri-stethoscope-line text-red-500 text-xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 font-heading">Diagnosis</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div className="space-y-4">
            {steps.map((step) => (
              <div key={step.num} className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-brand-50 border border-brand-100 rounded-xl">
                  <span className="text-xs font-bold text-brand-500 font-heading">{step.num}</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm font-heading">{step.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed font-body">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-5">
            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2 font-heading">
                <i className="ri-alarm-warning-line text-amber-500"></i>
                Diagnostic Challenges
              </h3>
              <ul className="space-y-3 text-sm text-gray-600 font-body">
                {['Extremely low prevalence means most clinicians have never encountered a case','Significant phenotypic overlap with Noonan syndrome and FMD2 complicates clinical diagnosis','Presentation varies widely — some features become more apparent with age','Fewer than 30 genetically confirmed cases worldwide as of 2025','No single pathognomonic feature — diagnosis requires integration of multiple clinical findings'].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-gray-400 mt-0.5 flex-shrink-0"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-test-tube-line text-emerald-600"></i>
                <h3 className="font-semibold text-gray-900 font-heading">Genetic Testing is the Only Confirmation</h3>
              </div>
              <p className="text-sm text-gray-600 font-body leading-relaxed mb-4">
                Clinical features alone cannot confirm CSCF. <strong className="text-gray-800">Whole exome sequencing (WES) identifying a pathogenic MAP3K7 variant is required.</strong> Trio-WES — testing the child and both parents — has confirmed every known case in the literature.
              </p>
              <a
                href="/#genetic-testing"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-800 transition-colors cursor-pointer font-body"
              >
                <i className="ri-arrow-right-line"></i>
                Learn how to pursue genetic testing
              </a>
            </div>

            <div className="bg-brand-50 border border-brand-100 rounded-2xl p-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 font-heading">
                <i className="ri-lightbulb-line text-brand-500"></i>
                When to Suspect CSCFS
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3 font-body">Consider CSCFS in a child presenting with <strong>two or more</strong> of the following:</p>
              <ul className="space-y-2 text-sm text-gray-600 font-body">
                {['Congenital heart disease (especially valvular)','Characteristic facial features','Cervical or thoracic vertebral fusion','Carpal or tarsal bone fusion','Conductive hearing loss','Failure to thrive / short stature','Brachydactyly with joint laxity'].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i className="ri-check-line text-brand-500 mt-0.5 flex-shrink-0"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-6 text-xs text-gray-400 border-t border-gray-100 pt-4 font-body">
          Sources: AbuBakr et al. 2020; Das &amp; Criscuolo 2025; Yang et al. 2025; Le Goff et al. 2016
        </div>
      </div>
    </section>
  );
}
